import type { ProjectStorage } from '@/persistence/ProjectStorage';
import { ProjectStore } from '@/persistence/ProjectStore';
import { uniqueProjectName } from '@/project/Project';

/**
 * Move work made before signing in into the account.
 *
 * Someone who has been using the tool anonymously has sites in this browser's IndexedDB. Signing in
 * switches the app to account storage, and without this their library appears to vanish — the sites
 * are still on the machine, but nothing shows them. That is the single worst moment in adding
 * accounts to an app that previously had none, so it is handled explicitly rather than left to the
 * user to notice.
 *
 * Three rules shape the implementation:
 *
 * 1. **Nothing is deleted.** The local copies stay exactly where they are. If the upload half-fails,
 *    or the user signs out, or they simply preferred it the old way, their work is still there. The
 *    local store is only ever read here.
 *
 * 2. **Nothing is overwritten.** A site already in the account under the same id is left alone —
 *    the account is the newer, authoritative copy in that case, most likely because this same
 *    browser uploaded it on a previous session. Only genuinely new ids are pushed.
 *
 * 3. **Names are made unique against the account, not the device.** Two machines both holding an
 *    "Untitled site" must not collide into one confusing pair of identical rows.
 *
 * Photos are deliberately not migrated. A gallery is hundreds of megabytes of PNGs, uploading it
 * silently on sign-in would be a startling use of someone's connection, and unlike a design
 * document a shot can be retaken. They stay on the device.
 */

export interface ImportProgress {
  /** Sites examined so far. */
  done: number;
  total: number;
  /** Name of the site currently being uploaded. */
  current: string;
}

export interface ImportResult {
  /** Sites uploaded to the account. */
  imported: number;
  /** Sites skipped because the account already held that id. */
  skipped: number;
  /** Sites that could not be uploaded, with the reason. */
  failed: { name: string; reason: string }[];
}

export interface LocalImportOptions {
  /** The account-backed store to upload into. */
  cloud: ProjectStorage;
  /** Defaults to this device's IndexedDB. Injectable for tests. */
  local?: ProjectStorage;
  onProgress?: (progress: ImportProgress) => void;
}

/** How many sites are sitting in local storage that the account does not have. */
export async function countLocalOnly(cloud: ProjectStorage, local?: ProjectStorage): Promise<number> {
  const store = local ?? new ProjectStore();
  try {
    const [localSummaries, cloudSummaries] = await Promise.all([
      store.summaries(),
      cloud.summaries(),
    ]);
    const known = new Set(cloudSummaries.map((summary) => summary.id));
    return localSummaries.filter((summary) => !known.has(summary.id)).length;
  } catch {
    // Offering the import is a courtesy; failing to count is not worth an error to the user.
    return 0;
  }
}

/**
 * Copy every local-only site into the account.
 *
 * Sequential rather than parallel: documents run to megabytes, the API is rate-limited, and a burst
 * of twenty concurrent uploads is exactly the shape that trips the limiter and leaves the library
 * half-copied. One at a time is slower and finishes.
 */
export async function importLocalProjects(options: LocalImportOptions): Promise<ImportResult> {
  const local = options.local ?? new ProjectStore();
  const result: ImportResult = { imported: 0, skipped: 0, failed: [] };

  const [localSummaries, cloudSummaries] = await Promise.all([
    local.summaries(),
    options.cloud.summaries(),
  ]);

  const knownIds = new Set(cloudSummaries.map((summary) => summary.id));
  const takenNames = cloudSummaries.map((summary) => summary.name);
  const pending = localSummaries.filter((summary) => !knownIds.has(summary.id));

  let done = 0;
  for (const summary of pending) {
    options.onProgress?.({ done, total: pending.length, current: summary.name });
    done += 1;

    try {
      const document = await local.get(summary.id);
      if (!document) {
        // Listed a moment ago and gone now — another tab deleted it. Not an error worth reporting.
        result.skipped += 1;
        continue;
      }

      const name = uniqueProjectName(document.name, takenNames);
      await options.cloud.save({ ...document, name });
      // Track it immediately so the next site in this same run cannot collide with it.
      takenNames.push(name);
      result.imported += 1;
    } catch (error) {
      result.failed.push({
        name: summary.name,
        reason: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  options.onProgress?.({ done, total: pending.length, current: '' });
  return result;
}

/** A sentence describing what an import did, for a toast. */
export function describeImport(result: ImportResult): string {
  const parts: string[] = [];
  if (result.imported > 0) {
    parts.push(`Copied ${String(result.imported)} site${result.imported === 1 ? '' : 's'} to your account`);
  }
  if (result.skipped > 0) parts.push(`${String(result.skipped)} already there`);
  if (result.failed.length > 0) {
    parts.push(`${String(result.failed.length)} could not be copied`);
  }
  if (parts.length === 0) return 'Nothing to copy — your account already has everything on this device.';
  return `${parts.join(' · ')}. The copies on this device were left untouched.`;
}
